/**
 * Spot a graded card wearing raw clothing.
 *
 * eBay gives a seller two different boxes: a grading block (company plus grade)
 * and a condition dropdown. They fill in one or the other, never both, which we
 * confirmed across 44,166 sold listings with zero exceptions. So a slab listed
 * by someone who typed the condition instead of the grade arrives looking
 * exactly like a raw card, and it drags the raw price up with it.
 *
 * The grader column is authoritative wherever it is filled. This only runs on
 * the rows that column calls raw. On the audit set it caught 49 of 11,385 such
 * rows with no false positives, and missed only 24 of 32,781 confirmed slabs.
 *
 * Two traps are deliberately handled and neither is theoretical:
 *
 *   ACE and TAG are grading companies and also Pokemon card mechanics. "ACE
 *   SPEC" and "TAG TEAM" appear in thousands of ordinary raw titles, so those
 *   letters only count when a grade follows them.
 *
 *   The bare word "graded" shows up in store names ("Graded & Scan Cards") and
 *   sales pitches ("Get Graded") far more often than in claims about the item,
 *   so it too needs a grade nearby.
 */

const GRADERS = [
  'PSA', 'BGS', 'BECKETT', 'CGC', 'SGC', 'HGA', 'GMA',
  'ISA', 'RCG', 'MNT', 'ARS', 'PCA', 'CGA', 'AGS', 'PGX', 'WCG',
];

const SHORT_GRADERS = ['ACE', 'TAG', 'GG'];

const GRADE = '(?:10|[1-9](?:\\.5)?)';

const PATTERNS = [
  // No boundary before the grade: sellers write "PSA9" and "PSA1!" as readily
  // as "PSA 9", and requiring the space missed real slabs.
  [new RegExp(`\\b(?:${GRADERS.join('|')})\\s*-?\\s*${GRADE}\\b`, 'i'), 'grader + grade'],
  [new RegExp(`\\b(?:${SHORT_GRADERS.join('|')})\\s*-?\\s*${GRADE}\\b`, 'i'), 'short grader + grade'],
  [new RegExp(`\\b(?:graded|slabbed)\\b.{0,18}?\\b${GRADE}\\b`, 'i'), 'graded + grade'],
  [/\bgem\s*(?:mt|mint)\s*10\b/i, 'gem mint 10'],
  [/\bpristine\s*10\b/i, 'pristine 10'],
  [new RegExp(`\\b(?:${GRADERS.join('|')})\\b(?!\\s*-?\\s*${GRADE})`, 'i'), 'grader named, no grade'],
];

// "PSA 10 ready", "would grade a 10" and "not graded" are raw cards describing
// an ambition. Matching those would throw away genuine raw comps, which is the
// opposite of the error this exists to fix.
const NOT_GRADED = new RegExp(
  '\\b(?:un-?graded|not\\s+graded|no\\s+grade|never\\s+graded|raw)\\b'
  + '|\\b(?:psa|bgs|cgc)\\s*(?:10|9)?\\s*(?:ready|worthy|candidate|contender|potential|bound|hopeful)\\b'
  + '|\\bwould\\s+grade\\b|\\bgrade\\s+(?:it|this)\\b|\\bfor\\s+grading\\b|\\bpre-?grade\\b',
  'i',
);

/**
 * @param {string} title listing title as eBay shows it
 * @returns {{ graded: boolean, why: string|null }}
 */
export function slabInTitle(title) {
  if (!title) return { graded: false, why: null };
  if (NOT_GRADED.test(title)) return { graded: false, why: 'explicitly raw' };
  for (const [pattern, why] of PATTERNS) {
    if (pattern.test(title)) return { graded: true, why };
  }
  return { graded: false, why: null };
}

/**
 * The grader and grade the title leads with.
 *
 * Sellers put the slab's identity at the front, because that is what the buyer
 * is searching for. Anything later in the title is context, a comparison, or a
 * pitch - and the pitch is the problem this exists for:
 *
 *   "SGC 9.5 1999 1st Edition DRAGONITE Pokemon Fossil Holo 4/62
 *    Graded 9.5 (PSA 10?) #4"          sold $1,408, arrived filed as PSA 10
 *
 * That card is an SGC 9.5. The "(PSA 10?)" is the seller guessing what it might
 * cross to, and the vendor's parser read it as the grade. One comp like that
 * among two genuine PSA 10 sales at $22,500 and $19,433 pulled the average from
 * about $20,967 down to $14,447.
 *
 * Reading only the first pair is the whole trick. Every pair in the title would
 * have found "PSA 10" here and agreed with the wrong answer.
 *
 * @param {string} title
 * @returns {{ company: string, grade: number }|null}
 */
export function leadingSlab(title) {
  if (!title) return null;
  if (NOT_GRADED.test(title)) return null;

  const pair = new RegExp(
    `\\b(${[...GRADERS, ...SHORT_GRADERS].join('|')})\\s*-?\\s*(${GRADE})\\b`,
    'i',
  ).exec(title);
  if (!pair) return null;

  const company = pair[1].toUpperCase() === 'BECKETT' ? 'BGS' : pair[1].toUpperCase();
  return { company, grade: parseFloat(pair[2]) };
}

export default slabInTitle;
